import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { sortByKey } from '../../businessLogicService/sortCriteria';
import { Button } from 'primereact/button';
import { FC, useState } from 'react';
import { DiagnosticUpsertStudentTargetGroupsForStudentMutation } from '../../graphQLOperations/__generated__/DiagnosticUpsertStudentTargetGroupsForStudentMutation.graphql';
import { useMutation } from 'react-relay';
import { upsertStudentTargetGroupsForStudent } from '../../graphQLOperations/Diagnostic';

interface Props {
  onRoadmapUpdated?: () => void;
}

const ROADMAP_UPDATE_ERROR = "We couldn't update your Roadmap. Please try again.";

export const DashboardNoRoadmapSelectedModal: FC<Props> = ({ onRoadmapUpdated = () => window.location.reload() }) => {
  const [roadmapDropdownValue, setRoadmapDropdownValue] = useState<{ id: string; name: string } | null>(null);
  const [roadmapUpdateFailed, setRoadmapUpdateFailed] = useState(false);
  const userRedux = useAppSelector(selectUser);
  const targetGroupsNodes =
    userRedux?.sites
      ?.map(
        site =>
          site?.targetGroups?.map(target => ({
            id: target?.id || '',
            name: target?.name || '',
          })) ?? [],
      )
      ?.flat() ?? [];

  let targetGroupTemplates = targetGroupsNodes?.map(target => ({ id: target?.id, name: target?.name }));
  targetGroupTemplates = [...targetGroupTemplates].sort(sortByKey('name'));

  const [upsertStudentTargetsMutation, loadingTargetUpdate] =
    useMutation<DiagnosticUpsertStudentTargetGroupsForStudentMutation>(upsertStudentTargetGroupsForStudent);

  const handleSubmit = () => {
    if (roadmapDropdownValue) {
      setRoadmapUpdateFailed(false);
      upsertStudentTargetsMutation({
        variables: {
          input: {
            studentTargetGroups: [
              {
                studentId: `StudentNode:${userRedux?.id}`,
                targetGroupId: roadmapDropdownValue?.id || '',
                isActive: true,
                isCurrent: true,
              },
            ],
          },
        },
        onCompleted: (response, errors) => {
          if (errors?.length || response.upsertStudentTargetGroupsForStudent.errors?.length) {
            setRoadmapUpdateFailed(true);
            return;
          }

          onRoadmapUpdated();
        },
        onError: () => setRoadmapUpdateFailed(true),
      });
    }
  };

  return (
    <Card className="col-10 col-offset-1 lg:col-6 lg:col-offset-3" id="diagnosticUIStep3">
      <div className="flex flex-column align-items-center gap-lg w-full" style={{ fontFamily: 'Poppins' }}>
        <h3 className="font-semibold">Heads up!</h3>
        <div className="w-9 text-center">
          <p className="text-lg text-center w-full pt-0 mt-0 text-center">
            {`
            Your school's Roadmap got a little update. But don't worry,`}
            <span className="font-semibold">{` all your work is still safe`}</span>
          </p>
          <p className="mx-auto text-lg text-center mt-5 mb-0 w-7">{`Pick your grade to refresh your Roadmap!`}</p>
        </div>

        <div className="flex justify-content-start align-items-cente mt-1 gap-lg">
          <h3 className="text-center font-semibold">Your grade:</h3>
          <Dropdown
            disabled={loadingTargetUpdate}
            optionLabel="name"
            value={roadmapDropdownValue}
            onChange={e => setRoadmapDropdownValue(e.target.value)}
            options={targetGroupTemplates}
            placeholder="Select your grade"
            aria-label="Select your grade"
          />
        </div>

        {roadmapUpdateFailed && (
          <p role="alert" className="text-red-500 text-center mt-0 mb-0">
            {ROADMAP_UPDATE_ERROR}
          </p>
        )}

        <Button
          className="mt-3"
          label="Go back to my homepage"
          onClick={handleSubmit}
          disabled={!roadmapDropdownValue || loadingTargetUpdate}
        />
      </div>
    </Card>
  );
};
